import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { PortfolioService } from '../../../services/portfolioService';

const FeaturedProject = () => {
  const [featuredProjects, setFeaturedProjects] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadFeaturedProjects = async () => {
      try {
        setLoading(true);
        const result = await PortfolioService?.getFeaturedProjects(3);

        if (result?.error) {
          setError(result?.error);
        } else {
          setFeaturedProjects(result?.data || []);
        }
      } catch (err) {
        setError('Failed to load featured projects');
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedProjects();
  }, []);

  useEffect(() => {
    if (featuredProjects?.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % featuredProjects?.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [featuredProjects?.length]);

  const handleNext = () => {
    setCurrentIndex(prev => (prev + 1) % featuredProjects?.length);
  };

  const handlePrevious = () => {
    setCurrentIndex(prev => (prev - 1 + featuredProjects?.length) % featuredProjects?.length);
  };

  const currentProject = featuredProjects?.[currentIndex];

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/10 rounded-full mb-6">
              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            </div>
            <p className="text-white/70">Loading featured projects...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error || !featuredProjects?.length) {
    return (
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-red-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Unable to Load Projects</h3>
            <p className="text-white/70">{error || 'No featured projects available at the moment.'}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-blue-900/20 via-slate-900 to-slate-900"></div>
      <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]"></div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Featured Project</h2>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto">Showcasing my latest and most impactful work</p>
          </motion.div>
        </div>

        {/* Featured Project Display */}
        <div className="relative">
          <AnimatePresence mode="wait">
            {currentProject && (
              <motion.div
                key={currentProject?.id}
                initial={{ opacity: 0, x: 100 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -100 }}
                transition={{ duration: 0.5 }}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center"
              >
                {/* Project Image */}
                <div className="relative">
                  <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-purple-600 p-1">
                    <div className="bg-white rounded-xl p-4">
                      <img
                        src={currentProject?.image_url || currentProject?.project_images?.[0]?.image_url || 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'}
                        alt={currentProject?.project_images?.[0]?.alt_text || `${currentProject?.title} project screenshot`}
                        className="w-full h-64 lg:h-80 object-cover rounded-lg"
                        onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800'; }}
                      />
                    </div>
                  </div>

                  {/* Project Stats */}
                  <div className="absolute -bottom-6 -right-6 bg-white rounded-xl p-4 shadow-xl">
                    <div className="flex items-center space-x-4">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-slate-900">{currentProject?.view_count || 0}</div>
                        <div className="text-xs text-slate-500">Views</div>
                      </div>
                      <div className="w-px h-8 bg-slate-200"></div>
                      <div className="text-center">
                        <div className="text-2xl font-bold text-green-600">{currentProject?.technologies?.length || 0}</div>
                        <div className="text-xs text-slate-500">Tech</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Project Content */}
                <div className="space-y-6">
                  <div>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mb-4">
                      <span className="capitalize">{currentProject?.category?.replace('-', ' ') || 'Project'}</span>
                    </div>
                    <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">{currentProject?.title}</h3>
                    <p className="text-lg text-blue-100 leading-relaxed">{currentProject?.long_description || currentProject?.description}</p>
                  </div>

                  {/* Technologies */}
                  {currentProject?.technologies?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-blue-200 mb-3 uppercase tracking-wide">Technologies Used</h4>
                      <div className="flex flex-wrap gap-2">
                        {currentProject?.technologies?.map((tech, index) => (
                          <span key={index} className="px-3 py-1 bg-white/10 backdrop-blur-sm text-white text-sm rounded-full border border-white/20">{tech}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Client Info */}
                  {currentProject?.client_name && (
                    <div className="flex items-center space-x-3 p-4 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
                      <Icon name="Building" size={20} className="text-blue-300" />
                      <div>
                        <div className="text-white font-medium">{currentProject?.client_name}</div>
                        <div className="text-blue-200 text-sm">Client</div>
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex flex-wrap gap-4">
                    {currentProject?.live_url && (
                      <Button as="a" href={currentProject?.live_url} target="_blank" rel="noopener noreferrer" className="bg-white text-slate-900 hover:bg-blue-50" iconName="ExternalLink" iconPosition="right">View Live Project</Button>
                    )}
                    {currentProject?.github_url && (
                      <Button as="a" href={currentProject?.github_url} target="_blank" rel="noopener noreferrer" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" iconName="Github" iconPosition="right">View Code</Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation Controls */}
          {featuredProjects?.length > 1 && (
            <div className="flex items-center justify-center mt-12 space-x-6">
              <button onClick={handlePrevious} className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"><Icon name="ChevronLeft" size={20} /></button>

              {/* Dots Indicator */}
              <div className="flex space-x-2">
                {featuredProjects?.map((_, index) => (
                  <button key={index} onClick={() => setCurrentIndex(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === currentIndex ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'}`} />
                ))}
              </div>

              <button onClick={handleNext} className="p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 transition-all duration-300"><Icon name="ChevronRight" size={20} /></button>
            </div>
          )}

          {/* Project Count */}
          <div className="text-center mt-8">
            <p className="text-blue-200 text-sm">Project {currentIndex + 1} of {featuredProjects?.length}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedProject;
